// Admin media grid: dense square thumbs, multi-select, delete / move / retry.
import React, { useEffect, useMemo, useState } from 'react';
import { api, fetchMedia, thumbUrl } from '../api.js';
import { fmtBytes } from '../text.js';
import { toast } from '../components/toast.jsx';
import { CheckIcon, PlayIcon } from '../components/icons.jsx';

export default function MediaManager() {
	const [categories, setCategories] = useState([]);
	const [current, setCurrent] = useState('');
	const [items, setItems] = useState([]);
	const [selected, setSelected] = useState(() => new Set());
	const [busy, setBusy] = useState(false);
	const [moveTarget, setMoveTarget] = useState('');

	const loadCategories = () => {
		api.categories()
			.then((cats) => {
				setCategories(cats);
				setCurrent((cur) => cur || cats[0]?.id || '');
			})
			.catch(() => toast('Kategorien konnten nicht geladen werden.', 'error'));
	};
	useEffect(loadCategories, []);

	const loadMedia = () => {
		if (!current) return;
		fetchMedia(current, null)
			.then((res) => setItems(res.items))
			.catch(() => toast('Medien konnten nicht geladen werden.', 'error'));
	};
	useEffect(() => {
		setSelected(new Set());
		if (!current) return;
		fetchMedia(current, null)
			.then((res) => setItems(res.items))
			.catch(() => toast('Medien konnten nicht geladen werden.', 'error'));
	}, [current]);

	const toggle = (id) => {
		setSelected((cur) => {
			const next = new Set(cur);
			if (next.has(id)) next.delete(id);
			else next.add(id);
			return next;
		});
	};

	const withBusy = async (fn) => {
		setBusy(true);
		try {
			await fn();
		} finally {
			setBusy(false);
		}
	};

	const deleteSelected = () =>
		withBusy(async () => {
			if (!window.confirm(`${selected.size} Element(e) endgültig löschen?`)) return;
			let failed = 0;
			for (const id of selected) {
				try {
					await api.deleteMedia(id);
				} catch {
					failed += 1;
				}
			}
			if (failed) toast(`${failed} konnten nicht gelöscht werden.`, 'error');
			setSelected(new Set());
			loadMedia();
			loadCategories();
		});

	const moveSelected = () =>
		withBusy(async () => {
			if (!moveTarget || moveTarget === current) return;
			let dupes = 0;
			let failed = 0;
			for (const id of selected) {
				try {
					await api.moveMedia(id, moveTarget);
				} catch (err) {
					if (err.code === 'duplicate_in_target') dupes += 1;
					else failed += 1;
				}
			}
			if (dupes) toast(`${dupes} übersprungen (bereits in Zielkategorie vorhanden).`, 'info');
			if (failed) toast(`${failed} konnten nicht verschoben werden.`, 'error');
			setSelected(new Set());
			loadMedia();
			loadCategories();
		});

	const retry = (id) =>
		withBusy(async () => {
			try {
				await api.retryMedia(id);
				toast('Verarbeitung neu gestartet.', 'info');
				loadMedia();
			} catch {
				toast('Neustart fehlgeschlagen.', 'error');
			}
		});

	const totalBytes = useMemo(() => items.reduce((s, i) => s + i.size, 0), [items]);

	return (
		<section className='am'>
			<div className='am__toolbar'>
				<select className='input am__select' value={current} onChange={(e) => setCurrent(e.target.value)} aria-label='Kategorie'>
					{categories.map((c) => (
						<option key={c.id} value={c.id}>
							{c.name} ({c.count})
						</option>
					))}
				</select>
				<span className='am__meta'>
					{items.length} Elemente · {fmtBytes(totalBytes)}
				</span>
				<span className='am__spacer' />
				<button type='button' className='btn btn--ghost am__btn' onClick={() => setSelected(new Set(items.map((i) => i.id)))}>
					Alle
				</button>
				<button type='button' className='btn btn--ghost am__btn' onClick={() => setSelected(new Set())}>
					Keine
				</button>
			</div>

			{selected.size > 0 && (
				<div className='am__actions'>
					<strong>{selected.size} ausgewählt</strong>
					<select className='input am__select' value={moveTarget} onChange={(e) => setMoveTarget(e.target.value)} aria-label='Zielkategorie'>
						<option value=''>Verschieben nach …</option>
						{categories
							.filter((c) => c.id !== current)
							.map((c) => (
								<option key={c.id} value={c.id}>
									{c.name}
								</option>
							))}
					</select>
					<button type='button' className='btn am__btn' onClick={moveSelected} disabled={busy || !moveTarget}>
						Verschieben
					</button>
					<button type='button' className='btn btn--danger am__btn' onClick={deleteSelected} disabled={busy}>
						Löschen
					</button>
				</div>
			)}

			<div className='am__grid'>
				{items.map((item) => (
					<div key={item.id} className={`am__cell ${selected.has(item.id) ? 'am__cell--selected' : ''}`}>
						<button type='button' className='am__thumbbtn' onClick={() => toggle(item.id)} aria-pressed={selected.has(item.id)} aria-label={item.originalName ?? item.id}>
							{item.status === 'ready' ? (
								<img className='am__thumb' src={thumbUrl(item)} alt='' loading='lazy' />
							) : (
								<span className={`am__state am__state--${item.status}`}>{item.status === 'processing' ? '…' : '✕'}</span>
							)}
							{item.type === 'video' && (
								<span className='am__play' aria-hidden='true'>
									<PlayIcon size={14} />
								</span>
							)}
							<span className={`am__check ${selected.has(item.id) ? 'am__check--on' : ''}`} aria-hidden='true'>
								<CheckIcon size={12} />
							</span>
						</button>
						<div className='am__cellmeta' title={item.originalName ?? ''}>
							<span className='am__cellname'>{item.originalName ?? item.id.slice(0, 8)}</span>
							<span className='am__cellinfo'>
								{fmtBytes(item.size)} · {item.uploadedBy}
								{item.downloads > 0 && <span title={`${item.downloads}× heruntergeladen`}> · ↓{item.downloads}</span>}
							</span>
							{item.status === 'failed' && item.type === 'video' && (
								<button type='button' className='am__retry' onClick={() => retry(item.id)}>
									Erneut verarbeiten
								</button>
							)}
						</div>
					</div>
				))}
				{items.length === 0 && <div className='am__empty'>Keine Medien in dieser Kategorie.</div>}
			</div>
		</section>
	);
}
